/* Room tool geometry (DOM-free): a dragged rectangle becomes four walls
   welded at the corners. Users think in CLEAR interior sizes ("my room is
   12 by 10"); walls are centerlines, so centerline = clear + thickness. */
import type { Doc, Wall } from './types.ts';
import { uid } from './utils.ts';
import { WELD_TOL } from './geom.ts';
import { applyWeld, clampOpenings, collectWeld } from './weld.ts';
import { solveConstraints } from './solver.ts';

/** Default wall thickness for rooms (2×4 + drywall). */
export const ROOM_TH = 4.5;
/** Rooms smaller than this on either side (centerline) are ignored — a slip, not a room. */
export const ROOM_MIN = 12;

export interface Rect { x1: number; y1: number; x2: number; y2: number }

/** Any two corners → x1<x2, y1<y2. */
export const normRect = (ax: number, ay: number, bx: number, by: number): Rect =>
  ({ x1: Math.min(ax, bx), y1: Math.min(ay, by), x2: Math.max(ax, bx), y2: Math.max(ay, by) });

/** Force the drag to a square (⇧): the longer side wins, signs preserved. */
export function squareDrag(ax: number, ay: number, bx: number, by: number): { bx: number; by: number } {
  const s = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
  return { bx: ax + Math.sign(bx - ax || 1) * s, by: ay + Math.sign(by - ay || 1) * s };
}

/** Snap the dragged corner so the INSIDE size lands on the grid (people want
    "12 by 10", not "12 by 10 and a half"). `snap` is the grid rounding
    (identity when snapping is off); direction of the drag is preserved. */
export function snapRoomCorner(ax: number, ay: number, rx: number, ry: number, th: number,
                               snap: (v: number) => number): { bx: number; by: number } {
  const sx = Math.sign(rx - ax) || 1, sy = Math.sign(ry - ay) || 1;
  const w = Math.max(0, snap(Math.abs(rx - ax) - th)), h = Math.max(0, snap(Math.abs(ry - ay) - th));
  return { bx: ax + sx * (w + th), by: ay + sy * (h + th) };
}

/** Clear interior of a centerline rect (what the user measures with a tape). */
export const clearSize = (r: Rect, th = ROOM_TH) => ({ w: r.x2 - r.x1 - th, h: r.y2 - r.y1 - th });

/** Centerline rect whose clear interior is clearW×clearH, centered at (cx,cy), on whole inches. */
export function rectFromClear(cx: number, cy: number, clearW: number, clearH: number, th = ROOM_TH): Rect {
  const w = clearW + th, h = clearH + th;
  const x1 = Math.round(cx - w / 2), y1 = Math.round(cy - h / 2);
  return { x1, y1, x2: x1 + Math.round(w * 2) / 2, y2: y1 + Math.round(h * 2) / 2 };
}

/** A selected rectangle: four axis-aligned walls welded at the corners. */
export interface RoomSel extends Rect { top: Wall; right: Wall; bottom: Wall; left: Wall }

const near = (a: number, b: number) => Math.abs(a - b) < WELD_TOL;

/** If these walls are exactly a closed axis-aligned rectangle (any order or
    direction), name its sides; otherwise null. */
export function detectRect(walls: Wall[]): RoomSel | null {
  if (walls.length !== 4) return null;
  const hs = walls.filter(w => near(w.y1, w.y2) && !near(w.x1, w.x2));
  const vs = walls.filter(w => near(w.x1, w.x2) && !near(w.y1, w.y2));
  if (hs.length !== 2 || vs.length !== 2) return null;
  const [top, bottom] = hs[0].y1 < hs[1].y1 ? hs : [hs[1], hs[0]];
  const [left, right] = vs[0].x1 < vs[1].x1 ? vs : [vs[1], vs[0]];
  const x1 = left.x1, x2 = right.x1, y1 = top.y1, y2 = bottom.y1;
  if (!(x2 - x1 >= ROOM_MIN && y2 - y1 >= ROOM_MIN)) return null;
  const spans = (w: Wall, a: number, b: number, axis: 'x' | 'y') => {
    const lo = Math.min(w[`${axis}1`], w[`${axis}2`]), hi = Math.max(w[`${axis}1`], w[`${axis}2`]);
    return near(lo, a) && near(hi, b);
  };
  if (!spans(top, x1, x2, 'x') || !spans(bottom, x1, x2, 'x') || !spans(left, y1, y2, 'y') || !spans(right, y1, y2, 'y')) return null;
  return { x1, y1, x2, y2, top, right, bottom, left };
}

/** Inside (face-to-face) size of a detected rectangle. */
export const roomInside = (r: RoomSel) =>
  ({ w: r.x2 - r.x1 - r.left.th / 2 - r.right.th / 2, h: r.y2 - r.y1 - r.top.th / 2 - r.bottom.th / 2 });

/** Resize a rectangle to a new inside size, keeping its top-left corner where
    it is: the right wall and/or bottom wall move, everything welded to their
    corners follows (adjoining rooms stretch), openings stay on their walls.
    Pass null to leave a dimension alone. */
export function resizeRect(doc: Doc, r: RoomSel, insideW: number | null, insideH: number | null): void {
  const cur = roomInside(r);
  const moves: { wall: Wall; dx: number; dy: number }[] = [];
  if (insideW !== null && insideW > 0 && !near(insideW, cur.w)) moves.push({ wall: r.right, dx: insideW - cur.w, dy: 0 });
  if (insideH !== null && insideH > 0 && !near(insideH, cur.h)) moves.push({ wall: r.bottom, dx: 0, dy: insideH - cur.h });
  for (const m of moves) {
    const w = m.wall;
    const weld = collectWeld(doc.walls, [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }], new Set([w.id]), false);
    w.x1 += m.dx; w.x2 += m.dx; w.y1 += m.dy; w.y2 += m.dy;
    applyWeld(weld, m.dx, m.dy);
    solveConstraints(doc, new Set([w.id]), [w.id]);
  }
  clampOpenings(doc);
}

/** Four walls clockwise from the top-left corner: top, right, bottom, left.
    Corner coordinates are shared exactly so the editor welds them.
    Returns null when the rect is too small to be a room. */
export function roomWalls(r: Rect, th = ROOM_TH): Wall[] | null {
  if (r.x2 - r.x1 < ROOM_MIN || r.y2 - r.y1 < ROOM_MIN) return null;
  const { x1, y1, x2, y2 } = r;
  return [
    { id: uid('w'), x1, y1, x2, y2: y1, th },
    { id: uid('w'), x1: x2, y1, x2, y2, th },
    { id: uid('w'), x1: x2, y1: y2, x2: x1, y2, th },
    { id: uid('w'), x1, y1: y2, x2: x1, y2: y1, th },
  ];
}
